import { forwardRef } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import styled from "@emotion/styled";
import * as SelectPrimitive from "@radix-ui/react-select";

import UIcon, { IconProps } from "lib/icons/untitled_ui/UIcon";
import Button from "../button/Button";
import ButtonSplit from "../button_split/ButtonSplit";
import { getFloatingContentStyles } from "../popover/Popover";

export type SelectProps<TFieldValues extends FieldValues = FieldValues> =
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root> & {
    control?: Control<TFieldValues>;
    name?: Path<TFieldValues>;
    children: React.ReactNode;
  };

export function Select<TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  children,
  ...props
}: SelectProps<TFieldValues>) {
  if (control && name) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <SelectPrimitive.Root {...props} value={field.value} onValueChange={field.onChange}>
            {children}
          </SelectPrimitive.Root>
        )}
      />
    );
  }

  return <SelectPrimitive.Root {...props}>{children}</SelectPrimitive.Root>;
}

type SelectTriggerProps = React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
  onClear?: () => void;
  icon?: IconProps["icon"];
};

export const SelectTrigger = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(({ children, onClear, icon, ...props }, ref) => {
  const button = (
    <StyledTrigger asChild ref={ref} {...props}>
      <Button
        buttonStyle="inverse"
        buttonType="primary"
        icon={icon}
        fullWidth
        justifyContent="left"
      >
        {children}
      </Button>
    </StyledTrigger>
  );
  if (onClear) {
    return (
      <ButtonSplit secondaryType="button">
        {button}
        <Button
          buttonStyle="inverse"
          buttonType="primary"
          icon="xClose"
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClear();
          }}
        />
      </ButtonSplit>
    );
  }
  return button;
});

SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

export const StyledTrigger = styled(SelectPrimitive.Trigger)(({ theme }) => ({
  all: "unset",
  display: "inline-flex",
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  cursor: "pointer",

  "&:focus": {
    outline: "none",
  },

  "&:disabled": {
    cursor: "not-allowed",
  },
  "&:hover": {
    backgroundColor: theme.colors.sysTextDefault + "0A", // 4% opacity
  },
  "&:active": {
    backgroundColor: theme.colors.sysTextDefault + "14", // 8% opacity
  },
}));

export const SelectValue = SelectPrimitive.Value;

export const SelectContent = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ children, position = "popper", ...props }, ref) => (
  <StyledContent ref={ref} position={position} {...props}>
    <SelectScrollUpButton />
    <StyledViewport position={position}>{children}</StyledViewport>
    <SelectScrollDownButton />
  </StyledContent>
));

SelectContent.displayName = SelectPrimitive.Content.displayName;

const StyledContent = styled(SelectPrimitive.Content)(({ theme }) => ({
  ...getFloatingContentStyles(theme),
  overflow: "hidden",
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  borderRadius: 4,
  width: "var(--radix-select-trigger-width)",
  transformOrigin: "var(--radix-select-content-transform-origin)",
}));

const StyledViewport = styled(SelectPrimitive.Viewport)<{ position: "popper" | "item-aligned" }>(
  ({ position }) => ({
    padding: 8,
    ...(position === "popper" && {
      width: "var(--radix-select-trigger-width)",
      maxHeight: "var(--radix-select-content-available-height)",
    }),
  }),
);

type SelectItemProps = React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
  icon?: IconProps;
};

export const SelectItem = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  SelectItemProps
>(({ children, icon, ...props }, ref) => {
  return (
    <StyledSelectItem ref={ref} {...props}>
      <ItemContent>
        {icon && <UIcon {...icon} />}
        <ItemText>
          <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
        </ItemText>
      </ItemContent>
      <CheckIcon>
        <StyledItemIndicator>
          <UIcon icon="checkOutlined" size={16} />
        </StyledItemIndicator>
      </CheckIcon>
    </StyledSelectItem>
  );
});

SelectItem.displayName = SelectPrimitive.Item.displayName;

const StyledSelectItem = styled(SelectPrimitive.Item)(({ theme }) => ({
  all: "unset",
  padding: "8px",
  display: "flex",
  alignItems: "center",
  gap: 8,
  cursor: "pointer",
  fontSize: 14,
  color: theme.colors.sysTextBlack,
  backgroundColor: "transparent",
  transition: "background-color 0.15s",

  "&:hover": {
    backgroundColor: theme.colors.sysTextDefault + "0A", // 4% opacity
  },

  "&[data-highlighted]": {
    backgroundColor: theme.colors.sysTextDefault + "14", // 8% opacity
  },

  "&[data-state=checked]": {
    backgroundColor: theme.colors.sysPrimarySubtle,
  },

  "&[data-disabled]": {
    color: theme.colors.sysTextInactive,
    pointerEvents: "none",
  },
}));

const ItemContent = styled.div({
  flex: 1,
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  padding: "0px 8px",
  gap: 8,
});

const ItemText = styled.div({
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const CheckIcon = styled.div({
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
});

const StyledItemIndicator = styled(SelectPrimitive.ItemIndicator)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.colors.sysPrimaryDefault,
}));

// SelectLabel - Label for a group of items
export const SelectLabel = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ ...props }, ref) => <StyledLabel ref={ref} {...props} />);

SelectLabel.displayName = SelectPrimitive.Label.displayName;

const StyledLabel = styled(SelectPrimitive.Label)(({ theme }) => ({
  padding: "8px 12px 4px",
  fontSize: 12,
  lineHeight: "133%",
  fontWeight: theme.font.fontWeightMedium,
  color: theme.colors.sysTextSecondary,
}));

export const SelectSeparator = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ ...props }, ref) => <StyledSeparator ref={ref} {...props} />);

SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

const StyledSeparator = styled(SelectPrimitive.Separator)(({ theme }) => ({
  height: 1,
  backgroundColor: theme.colors.sysBorderDecorative,
  margin: "8px 0",
}));

export const SelectGroup = SelectPrimitive.Group;

export const SelectScrollUpButton = forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ ...props }, ref) => (
  <StyledScrollButton ref={ref} {...props}>
    <UIcon icon="chevronUp" size={16} />
  </StyledScrollButton>
));

SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

export const SelectScrollDownButton = forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ ...props }, ref) => (
  <StyledScrollButton ref={ref} {...props}>
    <UIcon icon="chevronDown" size={16} />
  </StyledScrollButton>
));

SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const StyledScrollButton = styled(SelectPrimitive.ScrollDownButton)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: 25,
  backgroundColor: theme.colors.sysBackgroundDefault,
  color: theme.colors.sysTextSecondary,
  cursor: "pointer",
}));

export default Select;
